import React, { useState, useEffect } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { supabase, handleSupabaseError } from '@/lib/supabase';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription, CardFooter } from '@/components/ui/card';
import { Separator } from '@/components/ui/separator';
import { 
  Calendar, 
  Clock, 
  MapPin, 
  ArrowLeft, 
  Loader2, 
  AlertTriangle, 
  Lock, 
  Download,
  CreditCard
} from 'lucide-react';
import { LazyImage } from '@/components/ui/lazy-image';
import { RichTextViewer } from '@/components/ui/rich-text-viewer/RichTextViewer';
import { toast } from 'sonner';

interface PremiumItineraryDay {
  id: string;
  day_number: number;
  title: string;
  description: string | null;
  location: string | null;
}

interface Tag {
  id: string;
  name: string;
}

interface PremiumItinerary {
  id: string;
  title: string;
  description: string | null;
  destination: string | null;
  duration: number | null;
  best_season: string | null;
  featured_image: string | null;
  price: number;
  currency: string;
  itinerary_days: PremiumItineraryDay[];
  tags: Tag[];
}

export default function PremiumItineraryDetails() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [hasPurchased, setHasPurchased] = useState(false);
  const [isLoadingPurchaseStatus, setIsLoadingPurchaseStatus] = useState(true);
  const [isProcessingCheckout, setIsProcessingCheckout] = useState(false);

  const { data: itinerary, isLoading, error } = useQuery({
    queryKey: ['premium-itinerary', id],
    queryFn: async () => {
      try {
        console.log(`Fetching premium itinerary data for: ${id}`);
        
        const { data: itineraryData, error: itineraryError } = await supabase
          .from('premium_itineraries')
          .select('*')
          .eq('id', id)
          .eq('status', 'published')
          .maybeSingle();

        if (itineraryError) {
          console.error("Error fetching premium itinerary:", itineraryError);
          throw handleSupabaseError(itineraryError);
        }
        
        if (!itineraryData) {
          console.error("No premium itinerary found with id:", id);
          throw new Error('Premium itinerary not found');
        }

        console.log(`Successfully fetched premium itinerary: ${itineraryData.title}`);

        const { data: daysData, error: daysError } = await supabase
          .from('premium_itinerary_days')
          .select('*')
          .eq('premium_itinerary_id', itineraryData.id)
          .order('day_number', { ascending: true });

        if (daysError) {
          console.warn("Error fetching premium itinerary days:", daysError);
        }
        const days = daysError ? [] : daysData;

        const { data: tagData, error: tagError } = await supabase
          .from('premium_itinerary_tags')
          .select('tags:tag_id(id, name)')
          .eq('premium_itinerary_id', itineraryData.id);
          
        if (tagError) {
          console.warn("Error fetching premium itinerary tags:", tagError);
        }
        
        const tags = tagData 
          ? tagData.map(item => (item.tags as unknown as Tag)) 
          : [];

        return {
          ...itineraryData,
          itinerary_days: days,
          tags: tags
        };
      } catch (error) {
        console.error("Error in premium itinerary queryFn:", error);
        throw error;
      }
    },
    staleTime: 5 * 60 * 1000,
  });

  useEffect(() => {
    const checkPurchaseStatus = async () => {
      try {
        setIsLoadingPurchaseStatus(true);
        
        const { data: { user } } = await supabase.auth.getUser();
        
        if (!user) {
          setHasPurchased(false);
          return;
        }
        
        const { data, error } = await supabase.rpc('has_user_purchased_itinerary', {
          itinerary_id: id
        });
        
        if (error) {
          console.error("Error checking purchase status:", error);
          setHasPurchased(false);
        } else {
          setHasPurchased(!!data);
        }
      } catch (error) {
        console.error("Error checking purchase status:", error);
        setHasPurchased(false);
      } finally {
        setIsLoadingPurchaseStatus(false);
      }
    };
    
    if (id) {
      checkPurchaseStatus();
    }
  }, [id]);

  const formatCurrency = (price: number, currency: string) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: currency,
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(price);
  };

  const handlePurchase = async () => {
    try {
      setIsProcessingCheckout(true);
      
      const { data: { user } } = await supabase.auth.getUser();
      
      if (!user) {
        toast.error("Please log in to purchase this itinerary");
        navigate('/login');
        return;
      }
      
      const origin = window.location.origin;
      const successUrl = `${origin}/purchase-success`;
      const cancelUrl = `${origin}/premium-itineraries/${id}`;
      
      const { data: { session } } = await supabase.auth.getSession();
      const accessToken = session?.access_token;
      
      const response = await fetch('https://ypmennmpmhttnsgeviul.supabase.co/functions/v1/create-checkout-session', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${accessToken}`
        },
        body: JSON.stringify({
          itineraryId: id,
          itineraryTitle: itinerary?.title,
          price: itinerary?.price,
          currency: itinerary?.currency,
          userId: user.id,
          successUrl,
          cancelUrl
        })
      });
      
      const data = await response.json();
      
      if (data.error) {
        throw new Error(data.error);
      }
      
      window.location.href = data.url;
      
    } catch (error) {
      console.error("Error initiating checkout:", error);
      toast.error("Failed to initiate checkout. Please try again.");
    } finally {
      setIsProcessingCheckout(false);
    }
  };

  const handleDownload = () => {
    toast.info("PDF download functionality will be implemented in Stage 5");
  };

  if (isLoading) {
    return (
      <div className="container py-10">
        <div className="flex flex-col justify-center items-center py-20 space-y-4">
          <Loader2 className="h-10 w-10 animate-spin text-primary" />
          <p className="text-muted-foreground">Loading premium itinerary details...</p>
        </div>
      </div>
    );
  }

  if (error || !itinerary) {
    return (
      <div className="container py-10">
        <Card className="mx-auto max-w-3xl">
          <CardContent className="flex flex-col items-center justify-center py-10 space-y-6">
            <div className="flex items-center text-destructive">
              <AlertTriangle className="h-6 w-6 mr-2" />
              <p className="text-lg font-medium">
                {error instanceof Error ? error.message : 'Premium itinerary not found'}
              </p>
            </div>
            
            <p className="text-muted-foreground text-center max-w-md">
              We're having trouble loading this premium itinerary. This could be due to network issues or the itinerary may no longer be available.
            </p>
            
            <Button variant="outline" asChild>
              <Link to="/premium-itineraries">
                <ArrowLeft className="h-4 w-4 mr-2" /> Back to Premium Itineraries
              </Link>
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="container py-8 md:py-12">
      <div className="mb-6">
        <Button variant="outline" asChild>
          <Link to="/premium-itineraries">
            <ArrowLeft className="h-4 w-4 mr-2" /> Back to premium itineraries
          </Link>
        </Button>
      </div>

      <Card className="overflow-hidden mb-8">
        {itinerary.featured_image && (
          <div className="w-full h-64 md:h-96 overflow-hidden">
            <LazyImage 
              src={itinerary.featured_image} 
              alt={itinerary.title} 
              className="w-full h-full object-cover"
            />
          </div>
        )}
        <CardHeader>
          <div className="flex items-center justify-between">
            <CardTitle className="text-3xl">{itinerary.title}</CardTitle>
            <Badge className="text-lg px-3 py-1 bg-primary">
              {formatCurrency(itinerary.price, itinerary.currency)}
            </Badge>
          </div>
          {itinerary.description && (
            <CardDescription className="text-lg mt-2">
              <RichTextViewer content={itinerary.description} />
            </CardDescription>
          )}
          <div className="flex flex-wrap gap-4 text-muted-foreground text-sm mt-4">
            {itinerary.destination && (
              <div className="flex items-center">
                <MapPin className="h-4 w-4 mr-1" />
                <span>{itinerary.destination}</span>
              </div>
            )}
            {itinerary.duration && (
              <div className="flex items-center">
                <Clock className="h-4 w-4 mr-1" />
                <span>{itinerary.duration} {itinerary.duration === 1 ? 'day' : 'days'}</span>
              </div>
            )}
            {itinerary.best_season && (
              <div className="flex items-center">
                <Calendar className="h-4 w-4 mr-1" />
                <span>Best season: {itinerary.best_season}</span>
              </div>
            )}
          </div>
          
          {itinerary.tags && itinerary.tags.length > 0 && (
            <div className="flex flex-wrap gap-2 mt-4">
              {itinerary.tags.map(tag => (
                <Badge key={tag.id} variant="secondary">{tag.name}</Badge>
              ))}
            </div>
          )}
        </CardHeader>
        <CardFooter className="flex-col gap-4 sm:flex-row">
          {hasPurchased ? (
            <Button className="w-full sm:w-auto" onClick={handleDownload}>
              <Download className="mr-2 h-4 w-4" /> Download Itinerary
            </Button>
          ) : (
            <Button 
              className="w-full sm:w-auto" 
              onClick={handlePurchase}
              disabled={isProcessingCheckout}
            >
              {isProcessingCheckout ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Processing...
                </>
              ) : (
                <>
                  <CreditCard className="mr-2 h-4 w-4" /> 
                  Purchase for {formatCurrency(itinerary.price, itinerary.currency)}
                </>
              )}
            </Button>
          )}
        </CardFooter>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>Day-by-Day Itinerary</CardTitle>
          {!hasPurchased && (
            <CardDescription className="text-amber-600 dark:text-amber-400 flex items-center">
              <Lock className="h-4 w-4 mr-2" /> 
              Preview shows only the first 2 days. Purchase to access the full itinerary.
            </CardDescription>
          )}
        </CardHeader>
        <CardContent className="space-y-6">
          {itinerary.itinerary_days && itinerary.itinerary_days.length > 0 ? (
            <>
              {itinerary.itinerary_days
                .filter((day, index) => hasPurchased || index < 2)
                .map((day, index) => (
                  <div key={day.id} className="space-y-2">
                    {index > 0 && <Separator className="my-4" />}
                    <div className="flex items-center gap-2">
                      <Badge className="w-12 flex justify-center">Day {day.day_number}</Badge>
                      <h3 className="text-xl font-semibold">{day.title}</h3>
                    </div>
                    {day.location && (
                      <div className="flex items-center text-muted-foreground text-sm">
                        <MapPin className="h-4 w-4 mr-1" /> {day.location}
                      </div>
                    )}
                    {day.description && (
                      <div className="text-muted-foreground">
                        <RichTextViewer content={day.description} />
                      </div>
                    )}
                  </div>
                ))}
              
              {!hasPurchased && itinerary.itinerary_days.length > 2 && (
                <div className="border-t pt-6 mt-6">
                  <Card className="bg-muted/50">
                    <CardContent className="flex flex-col items-center justify-center p-6 text-center">
                      <Lock className="h-8 w-8 text-muted-foreground mb-2" />
                      <h3 className="text-lg font-medium mb-2">
                        Full Itinerary Locked
                      </h3>
                      <p className="text-muted-foreground mb-4">
                        Purchase this premium itinerary to unlock all {itinerary.duration} days and download a PDF version.
                      </p>
                      <Button 
                        onClick={handlePurchase} 
                        disabled={isProcessingCheckout}
                      >
                        {isProcessingCheckout ? (
                          <>
                            <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                            Processing...
                          </>
                        ) : (
                          <>
                            <CreditCard className="mr-2 h-4 w-4" /> 
                            Purchase for {formatCurrency(itinerary.price, itinerary.currency)}
                          </>
                        )}
                      </Button>
                    </CardContent>
                  </Card>
                </div>
              )}
            </>
          ) : (
            <p className="text-center text-muted-foreground py-8">No day-by-day details available for this itinerary.</p>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
